import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { AppSettings } from '../../types/settings';
import { InfoTip } from '@shared-ui/components';

type EmailProvider = NonNullable<AppSettings['emailProvider']>;

/**
 * Email-provider selection + Resend API key. SendGrid key still lives in
 * Azure app settings (legacy). When the provider is switched to Resend, the
 * backend reads `resendApiKey` from this doc; falls back to RESEND_API_KEY
 * env var only if the field is empty.
 */
export function Email() {
    const queryClient = useQueryClient();
    const [provider, setProvider] = useState<EmailProvider>('sendgrid');
    const [resendApiKey, setResendApiKey] = useState<string>('');
    const [keyVisible, setKeyVisible] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

    const { data: settings } = useQuery({
        queryKey: ['settings'],
        queryFn: adminApi.getSettings,
    });

    useEffect(() => {
        if (settings?.emailProvider) setProvider(settings.emailProvider);
        if (settings?.resendApiKey !== undefined) setResendApiKey(settings.resendApiKey);
    }, [settings]);

    const mutation = useMutation({
        mutationFn: (next: Partial<AppSettings>) => adminApi.updateSettings(next),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
    });

    const handleSave = () => {
        setSaveStatus('saving');
        const trimmed = resendApiKey.trim();
        const payload: Partial<AppSettings> = {
            emailProvider: provider,
            // Only include resendApiKey when the field has a value — sending
            // an empty string would clear the saved key.
            ...(trimmed.length > 0 ? { resendApiKey: trimmed } : {}),
        };
        mutation.mutate(payload, {
            onSuccess: () => {
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 2000);
            },
            onError: () => {
                setSaveStatus('error');
                setTimeout(() => setSaveStatus('idle'), 3000);
            },
        });
    };

    return (
        <div className="settings-section">
            <div className="section-header">
                <h2>Email</h2>
                <p className="section-description">
                    Provider used for outbound mail (student invites, reports). Changes apply
                    within the backend settings cache TTL (~2 minutes).
                </p>
            </div>

            <div className="rate-limit-tier">
                <h3 className="rate-limit-tier-title">Provider</h3>
                <div className="rate-limit-fields">
                    <div className="rate-limit-field">
                        <label className="rate-limit-label">
                            Email provider
                            <InfoTip text="SendGrid uses the SENDGRID_API_KEY env var. Resend reads resendApiKey from app settings (this page), falling back to RESEND_API_KEY env var." />
                        </label>
                        <select
                            className="rate-limit-input"
                            value={provider}
                            onChange={e => setProvider(e.target.value as EmailProvider)}
                        >
                            <option value="sendgrid">SendGrid</option>
                            <option value="resend">Resend</option>
                        </select>
                    </div>
                </div>
            </div>

            {provider === 'resend' && (
                <div className="rate-limit-tier">
                    <h3 className="rate-limit-tier-title">Resend</h3>
                    <div className="rate-limit-fields">
                        <div className="rate-limit-field">
                            <label className="rate-limit-label">
                                Resend API key
                                <InfoTip text="Get from resend.com → API Keys. Domain buzzbrain.ai must be verified on the Resend account first." />
                            </label>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input
                                    type={keyVisible ? 'text' : 'password'}
                                    className="rate-limit-input"
                                    value={resendApiKey}
                                    onChange={e => setResendApiKey(e.target.value)}
                                    placeholder="re_..."
                                    style={{ flex: 1 }}
                                />
                                <button
                                    type="button"
                                    className="rate-limit-save-btn"
                                    style={{ padding: '4px 10px' }}
                                    onClick={() => setKeyVisible(v => !v)}
                                >
                                    {keyVisible ? 'Hide' : 'Show'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="rate-limit-actions">
                <button
                    className="rate-limit-save-btn"
                    onClick={handleSave}
                    disabled={mutation.isPending}
                >
                    {mutation.isPending && saveStatus === 'saving' ? 'Saving...' : 'Save Email Settings'}
                </button>
                {saveStatus === 'saved' && <span className="status-saved">Saved!</span>}
                {saveStatus === 'error' && <span className="status-error">Failed to save</span>}
            </div>
        </div>
    );
}
